// Members' buckets the server can't reach (docs/STORAGE.md §11.2). The bucket's details and keys
// live only on devices and, sealed, in records the server stores as ciphertext: the location's
// own record under its location key (the owner's devices), and each library's under the
// library key (a read record for everyone who can read it, a write record for uploaders; none
// for read-only storage). Requests are signed here (SigV4, in the WASM core) and sent through
// the server's relay, which checks them and passes the ciphertext on.
import { s3Presign } from '../core/wasm'
import { decryptName, encryptName, type MetaKind } from './metacrypto'

export interface AccessKey {
  id: string
  secret: string
}

/** Where a bucket is. `prefix`: the folder in it hrth uses ("" for all of it). */
export interface Bucket {
  endpoint?: string
  region: string
  bucket: string
  prefix: string
}

/**
 * A member's bucket as their devices know it. No write key: read-only storage. `retired`:
 * keys it replaced that may still work at the provider (people who lost access may have
 * them) until the owner deletes them there.
 */
export interface BucketRecord extends Bucket {
  name: string
  read: AccessKey
  write?: AccessKey
  retired?: AccessKey[]
}

/** What a library carries for everyone who can read it. */
export interface ReadRecord extends Bucket {
  read: AccessKey
}

/** What a library carries for its uploaders. */
export interface WriteRecord {
  write: AccessKey
}

/** How long a signed request lasts: a relayed read grant lasts as long, at most. */
export const SIGNED_TTL = 6 * 3600

async function seal(key: string, kind: MetaKind, value: unknown): Promise<string> {
  return encryptName(key, kind, JSON.stringify(value))
}

async function open<T>(key: string | undefined, kind: MetaKind, sealed: string | undefined): Promise<T | null> {
  if (!key || !sealed) return null
  const json = await decryptName(key, kind, sealed)
  if (json === null) return null
  try {
    return JSON.parse(json) as T
  } catch {
    return null
  }
}

export const sealBucket = (key: string, location: string, rec: BucketRecord) => seal(key, `storage-location:${location}`, rec)
export const openBucket = (key: string | undefined, location: string, sealed: string | undefined) =>
  open<BucketRecord>(key, `storage-location:${location}`, sealed)

/** A library's records for a bucket: read, and write unless the storage is read-only. */
export async function sealLibraryStorage(
  libraryKey: string,
  library: string,
  rec: BucketRecord,
): Promise<{ storage: string; storage_write?: string }> {
  const read: ReadRecord = { endpoint: rec.endpoint, region: rec.region, bucket: rec.bucket, prefix: rec.prefix, read: rec.read }
  return {
    storage: await seal(libraryKey, `library-storage:${library}`, read),
    ...(rec.write ? { storage_write: await seal(libraryKey, `library-storage-write:${library}`, { write: rec.write }) } : {}),
  }
}

export const openRead = (libraryKey: string | undefined, library: string, sealed: string | undefined) =>
  open<ReadRecord>(libraryKey, `library-storage:${library}`, sealed)
export const openWrite = (libraryKey: string | undefined, library: string, sealed: string | undefined) =>
  open<WriteRecord>(libraryKey, `library-storage-write:${library}`, sealed)

/** A blob's object, inside hrth's folder of the bucket. */
export const blobPath = (library: string, blob: string) => `libraries/${library}/${blob}`

/** An object for checking a bucket at setup (the relay allows these to people who may attach storage). */
export function checkPath(): string {
  const hex = [...crypto.getRandomValues(new Uint8Array(new ArrayBuffer(16)))].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `.hrth-check/${hex}`
}

/** A request for one object, signed here (path-style, like the server's own S3 client). */
export function presign(
  b: Bucket,
  key: AccessKey,
  method: string,
  path: string,
  query: Record<string, string> = {},
  expires = SIGNED_TTL,
): string {
  const bucket = { ...(b.endpoint ? { endpoint: b.endpoint } : {}), region: b.region, bucket: b.bucket, prefix: b.prefix }
  return s3Presign(JSON.stringify(bucket), JSON.stringify(key), method, path, JSON.stringify(query), Date.now() / 1000, expires)
}

/** The `<UploadId>` in the answer to starting a multipart upload. */
export function uploadIdOf(xml: string): string | null {
  return /<UploadId>([^<]+)<\/UploadId>/.exec(xml)?.[1] ?? null
}

/** Parts and their ETags in a ListParts answer, and the marker for the next page (if any). */
export function partsOf(xml: string): { parts: Map<number, string>; next: string | null } {
  const parts = new Map<number, string>()
  for (const m of xml.matchAll(/<Part>([\s\S]*?)<\/Part>/g)) {
    const n = /<PartNumber>(\d+)<\/PartNumber>/.exec(m[1] ?? '')?.[1]
    const etag = /<ETag>([^<]+)<\/ETag>/.exec(m[1] ?? '')?.[1]
    if (n && etag) parts.set(Number(n), etag.replace(/&quot;/g, '"'))
  }
  const truncated = /<IsTruncated>true<\/IsTruncated>/.test(xml)
  const next = truncated ? (/<NextPartNumberMarker>(\d+)<\/NextPartNumberMarker>/.exec(xml)?.[1] ?? null) : null
  return { parts, next }
}

/** The body completing a multipart upload, parts in order. */
export function completeBody(etags: string[]): string {
  const parts = etags.map((e, i) => `<Part><PartNumber>${i + 1}</PartNumber><ETag>${e}</ETag></Part>`).join('')
  return `<CompleteMultipartUpload>${parts}</CompleteMultipartUpload>`
}

// Which libraries are in sealed buckets, and which library each blob belongs to: filled as
// libraries and their entries are opened, so a blob's URL can be asked for with a request
// signed here, wherever it's fetched (the page, the Service Worker, the apps, git).
const reads = new Map<string, ReadRecord>()
const blobLibraries = new Map<string, string>()

/** A library's read record (null: not in a sealed bucket, or no key here). */
export function rememberRead(library: string, rec: ReadRecord | null): void {
  if (rec) reads.set(library, rec)
  else reads.delete(library)
}

export function rememberBlobs(library: string, blobs: Iterable<string>): void {
  for (const b of blobs) blobLibraries.set(b, library)
}

const writes = new Map<string, { bucket: Bucket; write: AccessKey }>()

/** Where a library in a sealed bucket is and its write key (null: none here, or read-only). */
export function rememberWrite(library: string, read: ReadRecord | null, write: WriteRecord | null): void {
  if (read && write) writes.set(library, { bucket: read, write: write.write })
  else writes.delete(library)
}

/** Where to upload a library's files and the key to sign with, if it's in a sealed bucket. */
export function writeTarget(library: string): { library: string; bucket: Bucket; write: AccessKey } | null {
  const w = writes.get(library)
  return w ? { library, ...w } : null
}

/** A GET signed here for a blob in a sealed bucket; undefined when the server signs (or relays) on its own. */
export function signedGet(blob: string, library = blobLibraries.get(blob)): string | undefined {
  const rec = library ? reads.get(library) : undefined
  return rec && library ? presign(rec, rec.read, 'GET', blobPath(library, blob)) : undefined
}

interface RelayApi {
  relay(method: string, signed: string, body?: Blob | string): Promise<{ status: number; text: string }>
}

/**
 * Deletes these blobs' objects from a library's sealed bucket, which the server can't do
 * itself; nothing to do outside sealed buckets, or without the write key here (read-only
 * storage keeps its files, as an admin removing someone's library does).
 */
export async function deleteFiles(api: RelayApi, library: string, blobs: Iterable<string>): Promise<void> {
  const t = writeTarget(library)
  if (!t) return
  for (const blob of blobs) {
    const res = await api.relay('DELETE', presign(t.bucket, t.write, 'DELETE', blobPath(library, blob)))
    if (res.status >= 300 && res.status !== 404) throw new Error(`the bucket refused to delete a file (${res.status})`)
  }
}

/**
 * Checks a bucket from this device, through the relay, before attaching it: the write key
 * writes, the read key reads what it wrote and can't delete (or it isn't read-only), and the
 * test object is cleaned up. Read-only storage: the read key reads (a missing object is
 * fine) and can't write. Throws with what's wrong.
 */
export async function checkBucket(api: RelayApi, b: Bucket, read: AccessKey, write?: AccessKey): Promise<void> {
  const path = checkPath()
  const sign = (k: AccessKey, method: string) => presign(b, k, method, path, {}, 600)
  const probe = `hrth check ${path}`
  if (write) {
    const put = await api.relay('PUT', sign(write, 'PUT'), probe)
    if (put.status !== 200) throw new Error(`The write key can't write to the bucket (${put.status}). Check the bucket, region and key.`)
    try {
      const got = await api.relay('GET', sign(read, 'GET'))
      if (got.status !== 200 || got.text !== probe) throw new Error(`The read key can't read the bucket (${got.status}).`)
      const del = await api.relay('DELETE', sign(read, 'DELETE'))
      if (del.status < 300) throw new Error('The read key can delete files: make a read-only key, so people you share with can’t.')
    } finally {
      await api.relay('DELETE', sign(write, 'DELETE')).catch(() => undefined)
    }
    return
  }
  const got = await api.relay('GET', sign(read, 'GET'))
  if (got.status !== 404 && got.status !== 200) throw new Error(`The read key can't read the bucket (${got.status}). Check the bucket, region and key.`)
  const put = await api.relay('PUT', sign(read, 'PUT'), probe)
  if (put.status < 300) {
    await api.relay('DELETE', sign(read, 'DELETE')).catch(() => undefined)
    throw new Error('That key can write: for read-only storage, make a read-only key.')
  }
}

/**
 * Whether the provider has stopped accepting a key (deleted or disabled there): a GET it
 * signs is refused, where a key that works gets the object or "not found".
 */
export async function keyRevoked(api: RelayApi, b: Bucket, key: AccessKey): Promise<boolean> {
  const got = await api.relay('GET', presign(b, key, 'GET', checkPath(), {}, 600))
  if (got.status === 401 || got.status === 403) return true
  if (got.status === 200 || got.status === 404) return false
  throw new Error(`The bucket didn't answer as expected (${got.status})`)
}

/** A bucket's record with new keys: the ones they replace are kept as retired until revoked. */
export function replaceKeys(rec: BucketRecord, read: AccessKey, write: AccessKey | 'current' | null): BucketRecord {
  const w = write === 'current' ? rec.write : (write ?? undefined)
  const old = [rec.read, ...(rec.write ? [rec.write] : [])].filter((k) => k.id !== read.id && k.id !== w?.id)
  const retired = [...(rec.retired ?? []), ...old].filter((k, i, all) => all.findIndex((x) => x.id === k.id) === i)
  const next: BucketRecord = { ...rec, read, ...(retired.length ? { retired } : {}) }
  if (w) next.write = w
  else delete next.write
  if (!retired.length) delete next.retired
  return next
}
